import React from 'react'
import { Card, IconButton } from '../../ui'
import { CheckboxOwnerList } from './interactions/CheckboxOwnerList.jsx'
import { OwnerList } from './interactions/OwnerList.jsx'
import { StructuredEntryList } from './interactions/StructuredEntryList.jsx'
import { FeedbackEntryList } from './interactions/FeedbackEntryList.jsx'
import { ToggleList } from './interactions/ToggleList.jsx'
import { QuickList } from './interactions/QuickList.jsx'
import { DateHintList } from './interactions/DateHintList.jsx'
import { NumberedConstrainedList } from './interactions/NumberedConstrainedList.jsx'
import { FreeTextarea } from './interactions/FreeTextarea.jsx'
import { DualRating } from './interactions/DualRating.jsx'
import { SingleInput } from '../preparation/interactions/SingleInput.jsx'

const SESSION_COLOR = '#9b6cc4'

function resolveComponent(type) {
  const map = {
    'checkbox-list': CheckboxOwnerList,
    'owner-list': OwnerList,
    'structured-entry': StructuredEntryList,
    'feedback-entry': FeedbackEntryList,
    'toggle-list': ToggleList,
    'quick-list': QuickList,
    'date-hint-list': DateHintList,
    'numbered-list': NumberedConstrainedList,
    'single-input': SingleInput,
    'textarea': FreeTextarea,
    'dual-rating': DualRating,
  }
  return map[type] ?? null
}

function buildProps(cardDef, data, onChange, readOnly, onPullFromPrep, isManagerView) {
  const { type, config } = cardDef

  if (type === 'checkbox-list') {
    return { items: data?.items ?? [], onChange, readOnly, ...config }
  }
  if (type === 'owner-list') {
    return { items: data?.items ?? [], onChange, readOnly, ...config }
  }
  if (type === 'structured-entry') {
    return { items: data?.items ?? [], onChange, readOnly, ...config }
  }
  if (type === 'feedback-entry') {
    return { items: data?.items ?? [], onChange, readOnly, isManagerView, ...config }
  }
  if (type === 'toggle-list') {
    const canPull = onPullFromPrep && !data?.pulledFromPrep
    return {
      items: data?.items ?? [],
      onChange,
      readOnly,
      toggleMode: config.toggleMode,
      maxItems: config.maxItems,
      maxChars: config.maxChars,
      onPullFromPrep: canPull ? onPullFromPrep : undefined,
    }
  }
  if (type === 'quick-list') {
    return { items: data?.items ?? [], onChange, readOnly, ...config }
  }
  if (type === 'date-hint-list') {
    return { items: data?.items ?? [], onChange, readOnly, ...config }
  }
  if (type === 'numbered-list') {
    return { items: data?.items ?? [], onChange, readOnly, ...config }
  }
  if (type === 'single-input') {
    return { value: data, onChange, disabled: readOnly, ...config }
  }
  if (type === 'textarea') {
    return { text: data?.text ?? '', onChange, readOnly }
  }
  if (type === 'dual-rating') {
    return {
      reportRating: data?.reportRating ?? null,
      managerRating: data?.managerRating ?? null,
      isReport: !isManagerView,
      onChange,
      readOnly,
    }
  }
  return { data, onChange, readOnly }
}

export function SessionCard({ cardDef, data, onChange, readOnly = false, onRemove, onPullFromPrep, isManagerView = false }) {
  const Component = resolveComponent(cardDef.type)
  const props = buildProps(cardDef, data, onChange, readOnly, onPullFromPrep, isManagerView)

  return (
    <Card
      variant={readOnly ? 'readonly' : 'default'}
      data-testid={`session-card-${cardDef.id}`}
      style={{
        // In-session cards use a dashed border to signal "not yet finalised"
        // — the design language permits dashed borders for placeholder /
        // populatable containers (§5.1).
        borderStyle: 'dashed',
        padding: 0,
        minHeight: 192,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={styles.header}>
        <span style={{ ...styles.iconBadge, background: SESSION_COLOR + '18', color: SESSION_COLOR }}>{cardDef.icon ?? '✦'}</span>
        <div style={styles.headerText}>
          <span style={styles.label}>{cardDef.label}</span>
          <span style={styles.prompt}>{cardDef.prompt}</span>
        </div>
        <span style={{ flex: 1 }} />
        {!readOnly && <span style={styles.grip} aria-hidden>⠿</span>}
        {onRemove && !readOnly && (
          <IconButton ariaLabel={`Remove ${cardDef.label}`} onClick={() => onRemove(cardDef.id)}>×</IconButton>
        )}
      </div>
      <div style={{ ...styles.accentBar, background: SESSION_COLOR }} />
      {Component && <Component {...props} />}
    </Card>
  )
}

const styles = {
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '12px 12px 0',
  },
  iconBadge: {
    width: '28px', height: '28px', borderRadius: '6px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '13px', flexShrink: 0,
  },
  headerText: { display: 'flex', flexDirection: 'column', gap: '1px' },
  label: { fontSize: '12px', fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--text-primary)' },
  prompt: { fontSize: '11px', color: 'var(--text-secondary)', opacity: 0.8 },
  accentBar: {
    height: '3px', opacity: 0.25, margin: '6px 12px',
    borderRadius: '2px',
  },
  grip: { fontSize: '12px', opacity: 0.2, cursor: 'grab', userSelect: 'none', marginTop: '2px' },
}
